import { generateLevel } from '../generation/generateLevel';
import type { LevelIdentity } from '../model/identity';
import type { GeneratedLevel } from '../model/level';
import { deserializeLevel } from '../model/serialization';
import { isWorkerResponse, type WorkerRequest, type WorkerResponse } from './protocol';

/**
 * Client for the generation worker, with a synchronous fallback.
 *
 * Where a worker cannot be created (older browsers, blocked scripts, some embedded
 * webviews) generation runs on the main thread using the very same pure modules, so output
 * is identical — only responsiveness differs.
 */

export interface GenerationClientOptions {
  /** Force the main-thread path. Used by tests and by the developer lab. */
  readonly disableWorker?: boolean;
}

interface PendingRequest {
  resolve(level: GeneratedLevel): void;
  reject(error: Error): void;
}

export class GenerationClient {
  private worker: Worker | null = null;
  private workerFailed = false;
  private readonly pending = new Map<string, PendingRequest>();
  private counter = 0;
  private readonly disableWorker: boolean;

  constructor(options: GenerationClientOptions = {}) {
    this.disableWorker = options.disableWorker === true;
  }

  get usingWorker(): boolean {
    return this.worker !== null;
  }

  private ensureWorker(): Worker | null {
    if (this.disableWorker || this.workerFailed) return null;
    if (this.worker !== null) return this.worker;
    if (typeof Worker === 'undefined') {
      this.workerFailed = true;
      return null;
    }

    try {
      const worker = new Worker(new URL('./generation.worker.ts', import.meta.url), {
        type: 'module',
        name: 'arrows-generation',
      });
      worker.addEventListener('message', (event: MessageEvent<unknown>) => {
        this.handleResponse(event.data);
      });
      worker.addEventListener('error', () => {
        // Fail over permanently: a worker that cannot start will not start later either.
        this.failAllPending(new Error('Generation worker error'));
        this.workerFailed = true;
        this.worker = null;
      });
      this.worker = worker;
      return worker;
    } catch {
      this.workerFailed = true;
      return null;
    }
  }

  private handleResponse(data: unknown): void {
    if (!isWorkerResponse(data)) return;
    const response = data as WorkerResponse;
    const pending = this.pending.get(response.requestId);
    // A stale result (its request was cancelled or superseded) is simply dropped.
    if (pending === undefined) return;
    this.pending.delete(response.requestId);

    if (response.type === 'success') {
      try {
        pending.resolve(deserializeLevel(response.level));
      } catch (error) {
        pending.reject(error instanceof Error ? error : new Error(String(error)));
      }
    } else {
      pending.reject(
        new Error(`${response.errorCode}: ${response.details ?? 'generation failed'}`),
      );
    }
  }

  private failAllPending(error: Error): void {
    for (const pending of this.pending.values()) pending.reject(error);
    this.pending.clear();
  }

  /** Generates a level, preferring the worker and falling back to the main thread. */
  async generate(identity: LevelIdentity): Promise<GeneratedLevel> {
    const worker = this.ensureWorker();
    if (worker === null) return this.generateOnMainThread(identity);

    this.counter += 1;
    const requestId = `req-${this.counter}`;
    const request: WorkerRequest = { type: 'generate', requestId, identity };

    return new Promise<GeneratedLevel>((resolve, reject) => {
      this.pending.set(requestId, { resolve, reject });
      try {
        worker.postMessage(request);
      } catch (error) {
        this.pending.delete(requestId);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    }).catch(async (error: unknown) => {
      if (this.workerFailed) return this.generateOnMainThread(identity);
      throw error instanceof Error ? error : new Error(String(error));
    });
  }

  private generateOnMainThread(identity: LevelIdentity): Promise<GeneratedLevel> {
    try {
      return Promise.resolve(generateLevel(identity).level);
    } catch (error) {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }
  }

  /** Withdraws interest in a request; any later result for it is discarded. */
  cancel(requestId: string): void {
    this.pending.delete(requestId);
    this.worker?.postMessage({ type: 'cancel', requestId } satisfies WorkerRequest);
  }

  cancelAll(): void {
    for (const requestId of [...this.pending.keys()]) this.cancel(requestId);
  }

  dispose(): void {
    this.cancelAll();
    this.worker?.terminate();
    this.worker = null;
  }
}
